import { ConfirmDialog, DirtyConfirmDialog } from '@mgrvtechgroup/admin-ds';
import { useCallback, useEffect, useRef } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router';

/**
 * 새로고침·탭 닫기 경고 — 브라우저 beforeunload 기본 확인창(문구는 브라우저가 정한다).
 * 앱 안 이동으로는 입력이 사라지지 않는 화면(예: 모듈 저장소에 입력을 두는 위저드)은 이것만 쓴다.
 */
export function useLeaveWarning(when: boolean) {
  useEffect(() => {
    if (!when) {
      return;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [when]);
}

/**
 * 저장하지 않은 변경이 있는 채로 페이지를 떠나려 할 때의 이탈 가드(C21) — 페이지 단위 편집 화면 공용.
 * - 라우터 이동(다른 페이지·뒤로가기)은 useBlocker + 확인창으로 막는다.
 *   쿼리스트링만 바뀌는 이동은 이 화면이 그대로 남으므로 막지 않는다.
 * - 새로고침·탭 닫기는 useLeaveWarning(beforeunload)에 맡긴다.
 * 시트·다이얼로그 닫기는 이것 대신 admin-ds useDirtyConfirm + DirtyConfirmDialog를 쓴다.
 * useBlocker는 데이터 라우터(createBrowserRouter)에서만 동작한다.
 */
export function UnsavedGuard({
  when,
  warning,
  suppressConfirmation = false,
  autoProceedWhenClean = true,
  includeSearch = false,
}: {
  when: boolean;
  warning?: { target: string; action: string; consequence: string };
  suppressConfirmation?: boolean;
  autoProceedWhenClean?: boolean;
  includeSearch?: boolean;
}) {
  // [나가기] 확정 뒤 재평가에서 같은 확인창이 다시 뜨지 않도록 가드를 끈다.
  const leavingRef = useRef(false);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      when &&
      !leavingRef.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (includeSearch && currentLocation.search !== nextLocation.search)),
    [when, includeSearch],
  );
  const blocker = useBlocker(shouldBlock);
  useLeaveWarning(when);

  useEffect(() => {
    if (autoProceedWhenClean && !when && blocker.state === 'blocked') {
      leavingRef.current = true;
      blocker.proceed();
    }
  }, [autoProceedWhenClean, blocker, when]);

  const open = blocker.state === 'blocked' && !suppressConfirmation;
  const reset = (nextOpen: boolean) => {
    if (!nextOpen && blocker.state === 'blocked') blocker.reset();
  };
  const proceed = () => {
    if (blocker.state === 'blocked') {
      leavingRef.current = true;
      blocker.proceed();
    }
  };

  if (suppressConfirmation) return null;

  return warning ? (
    <ConfirmDialog
      open={open}
      onOpenChange={reset}
      target={warning.target}
      action={warning.action}
      consequence={warning.consequence}
      onConfirm={proceed}
    />
  ) : (
    <DirtyConfirmDialog open={open} onOpenChange={reset} onConfirm={proceed} />
  );
}
